import { Tabs } from '@inflearn/ds-react-next';
import { useLang } from '@inflearn/i18n-tools';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { USERS_URL } from '../../../../constants/urls';
import useCheckMyProfile from '../../../../hooks/useCheckMyProfile';
import { useCurrentUrl } from '../../../../hooks/useCurrentUrl';
import useInstructorProfileQuery from '../../../../hooks/useInstructorProfileQuery';
import useQueryParams from '../../../../hooks/useQueryParams';
import useUserProfileQuery from '../../../../hooks/useUserProfileQuery';

type NavigationLink = {
  label: string;
  path: string;
  getHref: (userId: string, slug?: string) => string;
  enabled: boolean | undefined;
};

export function UserTabs() {
  const { id } = useQueryParams();
  const { data: profile } = useUserProfileQuery({ id: Number(id) });

  if (!profile) {
    return null;
  }

  const isInstructor = profile.isInstructor;

  return isInstructor ? <InstructorTabs /> : <StudentTabs />;
}

function StudentTabs() {
  const { t } = useTranslation();
  const { isKo } = useLang();

  const { id } = useQueryParams();
  const { isMe } = useCheckMyProfile();

  const { data: profile } = useUserProfileQuery({ id: Number(id) });
  const hasRoadmap = profile?.hasRoadmaps;
  const hasClip = profile?.hasClips;
  const hasBlog = profile?.hasBlogs;
  const hasPost = profile?.hasPosts;
  const userSlug = profile?.slug;

  const NAVIGATION_LINKS: NavigationLink[] = [
    {
      label: t('홈'),
      path: '',
      getHref: (userId: string, slug?: string) => USERS_URL.HOME({ userId, slug }),
      enabled: true,
    },
    {
      label: '클립',
      path: 'clips',
      getHref: (userId: string) => USERS_URL.CLIPS(userId),
      enabled: (isMe || hasClip) && isKo,
    },
    {
      label: t('로드맵'),
      path: 'roadmaps',
      getHref: (userId: string) => USERS_URL.ROADMAPS(userId),
      enabled: (isMe || hasRoadmap) && isKo,
    },
    {
      label: t('게시글'),
      path: 'posts',
      getHref: (userId: string) => USERS_URL.POSTS(userId),
      enabled: isMe || hasPost,
    },
    {
      label: t('블로그'),
      path: 'blogs',
      getHref: (userId: string) => USERS_URL.BLOGS(userId),
      enabled: (isMe || hasBlog) && isKo,
    },
  ];

  return <TabsNav links={NAVIGATION_LINKS} userId={String(id)} userSlug={userSlug} />;
}

function InstructorTabs() {
  const { t } = useTranslation();
  const { isKo } = useLang();

  const { id } = useQueryParams();
  const { isMe } = useCheckMyProfile();

  const { data: profile } = useUserProfileQuery({ id: Number(id) });
  const { data: instructorProfile } = useInstructorProfileQuery({
    userId: String(id),
    isInstructor: true,
  });
  const hasRoadmap = instructorProfile?.hasRoadmap;
  const hasCourse = !!instructorProfile?.courseCount;
  const hasReview = !!instructorProfile?.reviewCount;
  const hasClip = profile?.hasClips;
  const hasBlog = profile?.hasBlogs;
  const hasPost = profile?.hasPosts;
  const userSlug = profile?.slug;

  const NAVIGATION_LINKS: NavigationLink[] = [
    {
      label: t('홈'),
      path: '',
      getHref: (userId: string, slug?: string) => USERS_URL.HOME({ userId, slug }),
      enabled: true,
    },
    {
      label: t('강의'),
      path: 'courses',
      getHref: (userId: string) => USERS_URL.COURSES(userId),
      enabled: hasCourse,
    },
    {
      label: '클립',
      path: 'clips',
      getHref: (userId: string) => USERS_URL.CLIPS(userId),
      enabled: (isMe || hasClip) && isKo,
    },
    {
      label: t('로드맵'),
      path: 'roadmaps',
      getHref: (userId: string) => USERS_URL.ROADMAPS(userId),
      enabled: (isMe || hasRoadmap) && isKo,
    },
    {
      label: t('수강평'),
      path: 'reviews',
      getHref: (userId: string) => USERS_URL.COURSE_REVIEWS(userId),
      enabled: hasReview,
    },
    {
      label: t('게시글'),
      path: 'posts',
      getHref: (userId: string) => USERS_URL.POSTS(userId),
      enabled: isMe || hasPost,
    },
    {
      label: t('블로그'),
      path: 'blogs',
      getHref: (userId: string) => USERS_URL.BLOGS(userId),
      enabled: (isMe || hasBlog) && isKo,
    },
  ];

  return <TabsNav links={NAVIGATION_LINKS} userId={String(id)} userSlug={userSlug} />;
}

function TabsNav({
  links,
  userId,
  userSlug,
}: {
  links: NavigationLink[];
  userId: string;
  userSlug?: string;
}) {
  const { currentUrlWithoutQueryString } = useCurrentUrl();

  return (
    <Tabs value={currentUrlWithoutQueryString} mb={24} wrap="nowrap">
      <Tabs.List>
        {links
          .filter(({ enabled }) => enabled)
          .map(({ label, getHref }) => {
            const href = getHref(userId, userSlug);

            return (
              <Tabs.Tab key={href} component={Link} href={href} value={href}>
                {label}
              </Tabs.Tab>
            );
          })}
      </Tabs.List>
    </Tabs>
  );
}
